import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import ManhwaCard from "@/components/feature/ManhwaCard";
import { categories, getManhwasByCategory } from "@/mocks/manhwa";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "all");
  const [sortBy, setSortBy] = useState("latest");

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const manhwas = getManhwasByCategory(activeCategory);

  const sortedManhwas = [...manhwas].sort((a, b) => {
    if (sortBy === "rating") return b.rating - a.rating;
    if (sortBy === "popular") return b.views - a.views;
    if (sortBy === "chapters") return b.chapters - a.chapters;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: currentCategory?.name || "全部分类" },
  ];

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${currentCategory?.name || "全部"} - 韩漫大全`,
    description: `韩漫大全${currentCategory?.name || "全部"}分类，精选好看的${currentCategory?.name || ""}韩国漫画免费在线观看。`,
    url: `https://www.manhwadaquan.com/category/${activeCategory}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: manhwas.slice(0, 10).map((m, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Book",
          name: m.title,
          author: { "@type": "Person", name: m.author },
          url: `https://www.manhwadaquan.com/manhwa/${m.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <Navbar />
      <main className="pt-20">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-serif">
              {currentCategory?.name || "全部漫画"}
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              探索精彩韩漫世界，发现您喜爱的作品
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? "bg-primary-500 text-white"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} text-sm`} />
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Sort Controls */}
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-foreground-800">{manhwas.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
              {[
                { value: "latest", label: "最新" },
                { value: "popular", label: "热门" },
                { value: "rating", label: "评分" },
                { value: "chapters", label: "章节" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                    sortBy === opt.value
                      ? "bg-background-50 text-foreground-900 shadow-sm"
                      : "text-foreground-500 hover:text-foreground-700"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Manhwa Grid */}
          <div className="mt-6 mb-14">
            {sortedManhwas.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {sortedManhwas.map((manhwa) => (
                  <ManhwaCard key={manhwa.id} {...manhwa} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300" />
                <p className="mt-4 text-foreground-500">该分类暂无作品</p>
                <Link
                  to="/category/all"
                  className="mt-4 inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  浏览全部漫画
                  <i className="ri-arrow-right-line ml-1" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}